import React, { useEffect, useRef, useState, useMemo } from 'react';
import Peer from 'simple-peer';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import Video from './Video';
import { socket } from '../socket';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';

// Styled Components for a modern, mobile-first UI
const PageContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100vh;
    height: 100dvh;
    width: 100%;
    background: var(--tg-bg);
    color: var(--tg-text);
    overflow: hidden;
`;

const MainContent = styled.div`
    display: flex;
    flex-grow: 1;
    overflow: hidden;
    flex-direction: column;

    @media (min-width: 768px) {
        flex-direction: row;
    }
`;

const VideoGrid = styled.div`
    flex-grow: 1;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 16px;
    padding: 16px;
    overflow-y: auto;
`;

const OneToOneStage = styled.div`
    position: relative;
    flex-grow: 1;
    padding: 12px;
    overflow: hidden;
`;

const OneToOneRemote = styled.div`
    width: 100%;
    height: 100%;
    border-radius: var(--tg-radius);
    overflow: hidden;
    background: #000;
`;

const OneToOneLocal = styled.div`
    position: absolute;
    right: calc(12px + var(--safe-right));
    bottom: calc(12px + var(--safe-bottom));
    width: 120px;
    height: 160px;
    border-radius: var(--tg-radius);
    overflow: hidden;
    background: #000;
    border: 1px solid var(--tg-border);
    box-shadow: var(--tg-shadow);

    @media (min-width: 768px) {
        width: 160px;
        height: 220px;
    }
`;

const ChatContainer = styled.div`
    width: 100%;
    height: 40%; /* On mobile, chat takes a portion of the height */
    background: var(--tg-surface);
    display: flex;
    flex-direction: column;
    border-top: 1px solid var(--tg-border);

    @media (min-width: 768px) {
        width: 300px;
        height: 100%;
        border-top: none;
        border-left: 1px solid var(--tg-border);
    }
`;

const MessagesContainer = styled.div`
    flex-grow: 1;
    padding: 16px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const Message = styled.div`
    padding: 8px 12px;
    border-radius: 18px;
    max-width: 80%;
    word-wrap: break-word;
    background-color: ${props => (props.isMine ? '#007bff' : '#3a3a3a')};
    align-self: ${props => (props.isMine ? 'flex-end' : 'flex-start')};
`;

const ChatForm = styled.form`
    display: flex;
    padding: 16px;
    gap: 8px;
`;

const ChatInput = styled.input`
    flex-grow: 1;
    padding: 10px;
    border-radius: 20px;
    border: 1px solid var(--tg-border);
    background: var(--tg-surface-2);
    color: var(--tg-text);
    font-size: 14px;
`;

const VideoContainer = styled.div`
    background-color: #000;
    border-radius: 12px;
    overflow: hidden;
    position: relative;
    width: 100%;
    height: 100%;
    max-width: 400px; /* Max size on larger screens */
    aspect-ratio: 4 / 3; /* Maintain aspect ratio */

    @media (max-width: 767px) {
        width: calc(50% - 8px); /* Two videos per row on mobile */
    }
`;

const ControlsContainer = styled.div`
    padding: 16px;
    padding-bottom: calc(16px + var(--safe-bottom));
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
    background: var(--tg-surface);
    flex-shrink: 0;
`;

const AddUserModal = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 300px;
    background: var(--tg-surface-2);
    border: 1px solid var(--tg-border);
    border-radius: var(--tg-radius);
    box-shadow: var(--tg-shadow);
    z-index: 100;
    padding: 16px;
`;

const UserList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    max-height: 200px;
    overflow-y: auto;
`;

const UserListItem = styled.li`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid var(--tg-border);
`;

const ControlButton = styled.button`
    padding: 12px;
    border-radius: 50%;
    border: none;
    background: ${props => (props.active ? 'rgba(255,255,255,0.16)' : 'rgba(255,255,255,0.10)')};
    color: var(--tg-text);
    font-size: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background-color 0.2s;

    &.danger {
        background: var(--tg-danger);
    }

    &:hover {
        filter: brightness(1.05);
        &.danger {
            filter: brightness(1.0);
        }
    }
`;

const Room = () => {
    const [peers, setPeers] = useState([]);
    const [isMuted, setIsMuted] = useState(false);
    const [isCameraOff, setIsCameraOff] = useState(false);
    const [messages, setMessages] = useState([]);
        const [chatInput, setChatInput] = useState('');
    const [roomData, setRoomData] = useState(null);
    const [allUsers, setAllUsers] = useState([]);
    const [showAddUser, setShowAddUser] = useState(false);
    const [mediaError, setMediaError] = useState('');
    const userStream = useRef();
        const userVideo = useRef();
    const peersRef = useRef([]);
        const { roomId } = useParams();
    const navigate = useNavigate();

    const currentUser = useMemo(() => {
        const token = localStorage.getItem('token');
        if (!token) return null;
        try {
            return jwtDecode(token);
        } catch {
            return null;
        }
    }, []);

    useEffect(() => {
        // Connect to socket and join rooms for chat functionality first
        if (!socket.connected) socket.connect();
        socket.emit("join-chat-room", roomId);

        const setupMediaAndPeers = async () => {
            let stream = null;
            try {
                stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
                userStream.current = stream;
                if (userVideo.current) {
                    userVideo.current.srcObject = stream;
                }
            } catch (err) {
                console.error("getUserMedia error:", err);
                setMediaError('Camera/Mic not found or permission denied. Video chat is disabled.');
                // Continue without video/audio stream
            }

            // Now join the video call room and set up peer connections
            socket.emit("join-room", roomId);

            socket.on("other-users", (users) => {
                const newPeers = [];
                users.forEach(userID => {
                    // Pass the stream, which can be null if media failed
                    const peer = createPeer(userID, socket.id, stream);
                    peersRef.current.push({ peerID: userID, peer });
                    newPeers.push({ peerID: userID, peer });
                });
                setPeers(newPeers);
            });

            socket.on("user-joined", (payload) => {
                const peer = addPeer(payload.signal, payload.callerID, stream);
                peersRef.current.push({ peerID: payload.callerID, peer });
                setPeers(prev => [...prev, { peerID: payload.callerID, peer }]);
            });

            socket.on("receiving-returned-signal", (payload) => {
                const item = peersRef.current.find(p => p.peerID === payload.id);
                if (item) {
                    item.peer.signal(payload.signal);
                }
            });
        };

        setupMediaAndPeers();

        socket.on('user-left', id => {
            const peerObj = peersRef.current.find(p => p.peerID === id);
            if (peerObj) peerObj.peer.destroy();
            const newPeers = peersRef.current.filter(p => p.peerID !== id);
            peersRef.current = newPeers;
            setPeers(newPeers);
        });

        const handleReceiveMessage = ({ senderUsername, message, roomId: messageRoomId }) => {
            if (messageRoomId === roomId) {
                setMessages(prev => [...prev, { sender: senderUsername, text: message }]);
            }
        };
        socket.on('receive-room-message', handleReceiveMessage);

        const handleRoomsListUpdate = (rooms) => {
            if (rooms[roomId]) {
                setRoomData(rooms[roomId]);
            }
        };
        socket.on('update-rooms-list', handleRoomsListUpdate);

        const fetchUsers = async () => {
            const token = localStorage.getItem('token');
            if (!token) return;
            try {
                const serverUrl = process.env.REACT_APP_SERVER_URL || 'https://connect-server-nfxv.onrender.com';
                const res = await axios.get(`${serverUrl}/api/users`, { headers: { Authorization: `Bearer ${token}` } });
                setAllUsers(Array.isArray(res.data) ? res.data : []);
            } catch (e) {
                console.error("Failed to fetch users", e);
            }
        };
        fetchUsers();

        return () => {
            if (userStream.current) {
                userStream.current.getTracks().forEach(track => track.stop());
            }
            socket.emit('leave-room', roomId);
            socket.off('other-users');
            socket.off('user-joined');
            socket.off('receiving-returned-signal');
            socket.off('user-left');
            socket.off('receive-room-message', handleReceiveMessage);
            socket.off('update-rooms-list', handleRoomsListUpdate);
        }
    }, [roomId]);

    function createPeer(userToSignal, callerID, stream) {
        const peer = new Peer({ initiator: true, trickle: false, stream });
        peer.on("signal", signal => {
            socket.emit("sending-signal", { userToSignal, callerID, signal });
        });
        return peer;
    }

    function addPeer(incomingSignal, callerID, stream) {
        const peer = new Peer({ initiator: false, trickle: false, stream });
        peer.on("signal", signal => {
            socket.emit("returning-signal", { signal, callerID });
        });
        peer.signal(incomingSignal);
        return peer;
    }

    const toggleMute = () => {
        if (userStream.current) {
            userStream.current.getAudioTracks()[0].enabled = !isMuted;
            setIsMuted(!isMuted);
        }
    }

    const toggleCamera = () => {
        if (userStream.current) {
            userStream.current.getVideoTracks()[0].enabled = !isCameraOff;
            setIsCameraOff(!isCameraOff);
        }
    }

    const leaveRoom = () => {
        navigate('/');
    }

    const isOneToOne = peers.length === 1;

            const handleAddUser = (usernameToAdd) => {
        socket.emit('add-user-to-room', { roomId, usernameToAdd });
        setShowAddUser(false);
    };

    const handleSendMessage = (e) => {
        e.preventDefault();
        if (chatInput.trim() && currentUser) {
            socket.emit('send-room-message', { roomId, message: chatInput });
            // Optimistically update UI
            setMessages(prev => [...prev, { sender: currentUser.username, text: chatInput }]);
            setChatInput('');
        }
    }

    return (
        <PageContainer>
            {mediaError ? (
                <div style={{
                    margin: '12px',
                    padding: '10px 12px',
                    borderRadius: '12px',
                    border: '1px solid var(--tg-border)',
                    background: 'var(--tg-surface)',
                    color: 'var(--tg-text)',
                    fontSize: '12px',
                    lineHeight: 1.4,
                }}>
                    {mediaError}
                </div>
            ) : null}
            <MainContent>
                {isOneToOne ? (
                    <OneToOneStage>
                        <OneToOneRemote>
                            <Video peer={peers[0].peer} />
                        </OneToOneRemote>
                        <OneToOneLocal>
                            <video style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted ref={userVideo} autoPlay playsInline />
                        </OneToOneLocal>
                    </OneToOneStage>
                ) : (
                    <VideoGrid>
                        <VideoContainer>
                            <video style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted ref={userVideo} autoPlay playsInline />
                        </VideoContainer>
                        {peers.map(({ peerID, peer }) => (
                            <VideoContainer key={peerID}>
                                <Video peer={peer} />
                            </VideoContainer>
                        ))}
                    </VideoGrid>
                )}
                <ChatContainer>
                    <MessagesContainer>
                                                {messages.map((msg, index) => (
                            <Message key={index} isMine={msg.sender === currentUser?.username}>
                                <strong>{msg.sender}: </strong>{msg.text}
                            </Message>
                        ))}
                    </MessagesContainer>
                    <ChatForm onSubmit={handleSendMessage}>
                                                <ChatInput
                            type="text"
                            value={chatInput}
                            onChange={(e) => setChatInput(e.target.value)}
                            placeholder="Type a message..."
                        />
                        {currentUser?.username === roomData?.creator && (
                            <ControlButton onClick={() => setShowAddUser(!showAddUser)}>+</ControlButton>
                        )}
                        <ControlButton type="submit">➢</ControlButton>
                    </ChatForm>
                </ChatContainer>
            </MainContent>
            <ControlsContainer>
                <ControlButton onClick={toggleMute} active={isMuted}>🎤</ControlButton>
                <ControlButton onClick={toggleCamera} active={isCameraOff}>📷</ControlButton>
                                <ControlButton className="danger" onClick={leaveRoom}>📞</ControlButton>
            </ControlsContainer>
            {showAddUser && (
                <AddUserModal>
                    <h4>Add User to Room</h4>
                    <UserList>
                        {allUsers
                            .filter(user => !roomData?.members.includes(user.username))
                            .map(user => (
                                <UserListItem key={user.id}>
                                    <span>{user.username}</span>
                                    <button onClick={() => handleAddUser(user.username)}>Add</button>
                                </UserListItem>
                            ))}
                    </UserList>
                    <button onClick={() => setShowAddUser(false)} style={{ marginTop: '16px', width: '100%' }}>Close</button>
                </AddUserModal>
            )}
        </PageContainer>
    );
};

export default Room;
